import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, set, onValue } from 'firebase/database';
import tiktokAPI from '../services/tiktokAPI';

export default function RealTimeStats() {
  const [username, setUsername] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [liveStats, setLiveStats] = useState({
    isLive: false,
    viewerCount: 0,
    likeCount: 0,
    shareCount: 0,
    commentCount: 0,
    giftCount: 0,
    diamonds: 0,
    followers: 0,
    totalViewers: 0,
    streamDuration: 0,
    lastUpdated: null,
    username: '',
    nickname: '',
    avatar: '',
    verified: false
  });
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // تحميل اسم المستخدم من Firebase
  useEffect(() => {
    const usernameRef = ref(db, 'tiktokUsername');
    const unsubscribe = onValue(usernameRef, (snapshot) => {
      if (snapshot.exists()) {
        const savedUsername = snapshot.val();
        setUsername(savedUsername);
        if (savedUsername && isConnected) {
          fetchTikTokStats(savedUsername);
        }
      }
    });

    return () => unsubscribe();
  }, [isConnected]);

  // حفظ اسم المستخدم في Firebase
  const saveUsername = async (newUsername) => {
    try {
      await set(ref(db, 'tiktokUsername'), newUsername);
    } catch (error) {
      console.error('خطأ في حفظ اسم المستخدم:', error);
    }
  };

  // جلب إحصائيات TikTok الحقيقية
  const fetchTikTokStats = async (targetUsername = username) => {
    if (!targetUsername.trim()) {
      setError('يرجى إدخال اسم المستخدم');
      return;
    }

    const validation = tiktokAPI.validateUsername(targetUsername);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setIsLoading(true);
    setError('');
    setConnectionStatus('connecting');

    try {
      const result = await tiktokAPI.getLiveStats(validation.username);
      
      if (result.success) {
        const stats = {
          ...result.data,
          lastUpdated: new Date().toISOString(),
          totalViewers: result.data.viewerCount || 0
        };
        
        setLiveStats(stats);
        setIsConnected(true);
        setConnectionStatus('connected');

        // حفظ الإحصائيات في Firebase
        await set(ref(db, 'tiktokLiveStats'), stats);
        
        console.log('تم جلب إحصائيات TikTok بنجاح:', stats);
      } else {
        throw new Error(result.error || 'فشل في جلب الإحصائيات');
      }

    } catch (error) {
      console.error('خطأ في جلب إحصائيات TikTok:', error);
      setError(`خطأ في جلب الإحصائيات: ${error.message}`);
      setConnectionStatus('error');
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  // تحديث الإحصائيات كل 30 ثانية
  useEffect(() => {
    if (!isConnected || !username || !autoRefresh) return;

    const interval = setInterval(() => {
      fetchTikTokStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [isConnected, username, autoRefresh]);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return '#28a745';
      case 'connecting': return '#ffc107';
      case 'error': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'متصل';
      case 'connecting': return 'جاري الاتصال...';
      case 'error': return 'خطأ في الاتصال';
      default: return 'غير متصل';
    }
  };

  return (
    <div className="real-time-stats" style={{
      background: 'var(--bg-secondary)',
      borderRadius: '15px',
      padding: '20px',
      boxShadow: '0 4px 15px var(--shadow-color)',
      border: '1px solid var(--border-color)'
    }}>
      <div className="stats-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h3 style={{
          margin: 0,
          color: 'var(--text-primary)',
          fontSize: '18px',
          fontWeight: 'bold'
        }}>
          📊 إحصائيات TikTok المباشرة
        </h3>
        
        <div className="connection-info" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '12px',
            color: getStatusColor(),
            fontWeight: 'bold'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: getStatusColor(),
              animation: connectionStatus === 'connecting' ? 'pulse 1s infinite' : 'none'
            }}></div>
            {getStatusText()}
          </div>
        </div>
      </div>

      {/* إدخال اسم المستخدم */}
      <div className="username-input" style={{
        marginBottom: '20px',
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="أدخل اسم مستخدم TikTok"
          style={{
            flex: 1,
            minWidth: '200px',
            padding: '10px 15px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}
        />
        
        <button
          onClick={() => {
            saveUsername(username);
            fetchTikTokStats();
          }}
          disabled={isLoading || !username.trim()}
          style={{
            padding: '10px 20px',
            background: 'var(--button-bg)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? 'جاري التحميل...' : 'تحديث الإحصائيات'}
        </button>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '14px',
          color: 'var(--text-primary)',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          تحديث تلقائي
        </label>
      </div>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '10px',
          borderRadius: '8px',
          marginBottom: '15px',
          fontSize: '14px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* إحصائيات البث المباشر */}
      {liveStats.username && (
        <div className="live-stats-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '20px'
        }}>
          {/* حالة البث */}
          <div style={{
            background: liveStats.isLive ? '#d4edda' : '#f8d7da',
            color: liveStats.isLive ? '#155724' : '#721c24',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>
              {liveStats.isLive ? '🔴' : '⚫'}
            </div>
            {liveStats.isLive ? 'بث مباشر' : 'غير متصل'}
          </div>

          {/* عدد المشاهدين */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>👥</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatNumber(liveStats.viewerCount)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>مشاهد</div>
          </div>

          {/* الإعجابات */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>❤️</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatNumber(liveStats.likeCount)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>إعجاب</div>
          </div>

          {/* الماس */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>💎</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatNumber(liveStats.diamonds)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>ماس</div>
          </div>

          {/* الهدايا */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>🎁</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatNumber(liveStats.giftCount)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>هدية</div>
          </div>

          {/* التعليقات */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>💬</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatNumber(liveStats.commentCount)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>تعليق</div>
          </div>

          {/* المشاركات */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>📤</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatNumber(liveStats.shareCount)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>مشاركة</div>
          </div>

          {/* المتابعون */}
          <div style={{
            background: 'var(--bg-primary)',
            padding: '15px',
            borderRadius: '10px',
            textAlign: 'center',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '5px' }}>👤</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {formatNumber(liveStats.followers)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>متابع</div>
          </div>
        </div>
      )}

      {/* معلومات إضافية */}
      {liveStats.isLive && (
        <div className="additional-info" style={{
          background: 'var(--bg-primary)',
          padding: '15px',
          borderRadius: '10px',
          border: '1px solid var(--border-color)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '15px'
          }}>
            <div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                مدة البث
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {formatDuration(liveStats.streamDuration)}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                إجمالي المشاهدين
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {formatNumber(liveStats.totalViewers)}
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                آخر تحديث
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {liveStats.lastUpdated ? new Date(liveStats.lastUpdated).toLocaleTimeString('ar-SA') : 'غير متوفر'}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
} 